import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { DecryptToken } from "../functions/commons";
import { LocalStorageUtils } from "../helpers/LocalStorage";
import {
  DataType,
  DataDBType,
  IndustriesType,
  ProfessionType,
  ProfileJobCriteriaType,
  ProfileType,
  ResponseTypeIndustriesDB,
  User,
  UserLoginType,
  UserSignupType,
  SkillsReqType,
  SkillsType,
  LoginResponseType,
  ProfileStatusType,
} from "../interfaces/user";
import { BASE_URL } from "../constants/stringConstants";

export const appApi = createApi({
  reducerPath: "app",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState, endpoint }) => {
      headers.set("Accept", "application/json");
      const token = LocalStorageUtils.getItem("auth_token");
      if (token && endpoint !== "/auth/login" && endpoint !== "/auth/signup") {
        headers.set("Authorization", `Bearer ${JSON.parse(token)}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    signupUser: builder.mutation<{}, UserSignupType>({
      query: (body) => ({
        url: `user/createCandidate`,
        method: "POST",
        body,
      }),
    }),

    loginUser: builder.mutation<{ id: string }, UserLoginType>({
      query: (body) => ({
        url: `user/login`,
        method: "POST",
        body,
      }),

      transformResponse: async (response: LoginResponseType) => ({
        ...(await DecryptToken(response.token)),
      }),
    }),

    verifyEmail: builder.mutation<any, any>({
      query: (data) => ({
        url: `/user/emailTokenVerification`,
        method: "POST",
        body: data,
      }),
    }),

    forgotPassword: builder.mutation<{}, any>({
      query: (body) => ({
        url: `user/forgetPasswordEmail`,
        method: "POST",
        body,
      }),
    }),

    verifyToken: builder.mutation<{}, any>({
      query: (body) => ({
        url: `user/forgetPasswordTokenVerification`,
        method: "POST",
        body,
      }),
    }),

    resetPassword: builder.mutation<{}, any>({
      query: (body) => ({
        url: `user/resetPassword`,
        method: "POST",
        body,
      }),
    }),

    profileCompleteCheck: builder.query<ProfileStatusType, string | number | undefined>({
      query: (id) => ({
        url: `candidate-profile-completness/${id}`,
        method: "Get",
      }),
    }),

    updateUser: builder.mutation<{}, ProfileType>({
      query: ({ param, ...rest }) => ({
        url: `user/${param}`,
        method: "PATCH",
        body: rest,
      }),
    }),

    updateProfile: builder.mutation<{}, ProfileType>({
      query: ({ param, ...rest }) => ({
        url: `candidate/${param}`,
        method: "PATCH",
        body: rest,
      }),
    }),

    updateProfileJobCriteria: builder.mutation<{}, ProfileJobCriteriaType>({
      query: ({ param, ...rest }) => ({
        url: `candidates/${param}/candidate-job-criteria`,
        method: "POST",
        body: rest,
      }),
    }),

    updateProfileJobCriteriaPatch: builder.mutation<{}, ProfileJobCriteriaType>({
      query: ({ param, ...rest }) => ({
        url: `candidates/${param}/candidate-job-criteria`,
        method: "PATCH",
        body: rest,
      }),
    }),

    getIndustries: builder.mutation<DataType[], IndustriesType>({
      query: ({ filterDetails }) => ({
        url: `industries?filter=${filterDetails}`,
        method: "GET",
      }),
    }),

    getDataFromDB: builder.mutation<ResponseTypeIndustriesDB, DataDBType>({
      query: ({ param, filterDetails }) => ({
        url: `candidate/${param}?filter=${filterDetails}`,
        method: "GET",
      }),
    }),

    getProfessions: builder.mutation<ProfessionType[], string>({
      query: (industryIds) => ({
        url: `industry/${industryIds}/professions`,
        method: "GET",
      }),
    }),

    getSkills: builder.mutation<SkillsType[], SkillsReqType>({
      query: (data) => ({
        url: `profession-with-skills`,
        method: "POST",
        body: data,
      }),
    }),

    uploadImage: builder.mutation<any, any>({
      query: (data) => ({
        url: `attachments/upload`,
        method: "POST",
        body: data,
      }),
    }),

    getInterviewsListing: builder.mutation<any, any>({
      query: (id) => ({
        url: `candidate/${id}/interviews`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useSignupUserMutation,
  useLoginUserMutation,
  useVerifyEmailMutation,
  useProfileCompleteCheckQuery,
  useLazyProfileCompleteCheckQuery,
  useUpdateProfileMutation,
  useUpdateProfileJobCriteriaMutation,
  useUpdateProfileJobCriteriaPatchMutation,
  useGetIndustriesMutation,
  useGetDataFromDBMutation,
  useGetProfessionsMutation,
  useGetSkillsMutation,
  useUpdateUserMutation,
  useUploadImageMutation,
  useGetInterviewsListingMutation,
  ////
  useForgotPasswordMutation,
  useVerifyTokenMutation,
  useResetPasswordMutation,
} = appApi;

// 'https://123.123.123.123/goods/121'
